/**
 * The platform's HTTP routes: registry, sessions, the ledger and participant admin. These
 * are core's, not any game's, so they stay HTTP rather than game actions.
 */

import type { ConfigForm, ShowCategory } from '@gsf/contract';
import { request } from './http';

export { ApiError } from './http';

/** A count for all time and for the last `recentDays`. */
export interface Tally {
  total: number;
  recent: number;
}

/** `GET /api/admin/stats`; the site admin only. */
export interface SiteStats {
  users: Tally;
  gamesPlayed: Tally;
  gamesFinished: Tally;
  playNowGames: Tally;
  players: Tally;
  signedInPlayers: Tally;
  questions: Tally;
  friendAdds: Tally;
}

/** Everything `GET /api/admin/stats` answers. */
export interface SiteReport {
  stats: SiteStats;
  recentDays: number;
  activeUsers: { week: number; month: number };
  newestAccounts: { id: string; displayName: string; email: string | null; createdAt: number }[];
  playsByGame: { gameId: string; plays: number }[];
  topCategories: { category: string; questions: number }[];
  live: {
    code: string;
    title: string;
    gameId: string | null;
    players: number;
    connected: number;
    playNow: boolean;
    createdAt: number;
  }[];
  server: {
    version: string;
    node: string;
    uptimeSeconds: number;
    rssBytes: number;
    databaseBytes: number;
    expiredLogins: number;
    armedTimers: number;
  };
}

/** One session on this server, as the create screen lists them. */
export interface SessionSummary {
  id: string;
  code: string;
  title: string;
  status: string;
  /** `lobby` · `segment` · `interstitial` · `results`. */
  stage: string;
  /** The game of the live segment; changes as a show runs. */
  gameId: string;
  segments: number;
  playerCount: number;
  joinUrl: string;
  /** Null for an unowned (dev seed) show; the list holds only yours and those. */
  ownerAccountId: string | null;
}

/** A content pack, with its owner and how many items (clues) it holds. */
export interface PackSummary {
  id: string;
  gameId: string;
  title: string;
  ownerAccountId: string | null;
  itemCount: number;
}

/** A registered game, for the picker. */
export interface GameManifestSummary {
  id: string;
  version: string;
  name: string;
  blurb: string;
  estimatedMinutes: number;
  players: { min: number; max: number };
  capabilities: { host: 'required' | 'optional' | 'none'; needsContent?: boolean };
  preset: { title: string; description: string };
  category: ShowCategory;
  /** Derived from the game's `configSchema`. Empty `fields` means nothing to set up. */
  configForm: ConfigForm;
}

/** One round of a saved show, plus what only the registry knows. */
export interface FormatStep {
  id: string;
  index: number;
  gameId: string;
  gameName: string;
  title: string;
  config: Record<string, unknown>;
  contentPackId: string | null;
  estimatedMinutes: number;
  missing: boolean;
}

/**
 * A saved show. `isBuiltIn` marks a read-only preset owned by the system account.
 * `problems` is empty when the show can start, else sentences to show by a disabled button.
 */
export interface FormatSummary {
  id: string;
  title: string;
  description: string;
  isBuiltIn: boolean;
  estimatedMinutes: number;
  hostMode: 'hosted' | 'unhosted';
  /** `'mixed'` means the rounds' games disagree, or one is not installed. */
  category: ShowCategory | 'mixed';
  steps: FormatStep[];
  problems: string[];
}

/** One round, as the builder posts it. `config` is the host's answers, unstringified. */
export interface StepDraft {
  gameId: string;
  packId?: string | null;
  title?: string | null;
  config?: Record<string, unknown>;
}

/** The only time the display and host-resume tokens are served. */
export interface CreatedSession {
  id: string;
  code: string;
  title: string;
  gameId: string;
  /** `'unhosted'` means "hand out the code and sit down". */
  hostMode: 'hosted' | 'unhosted';
  joinUrl: string;
  displayToken: string;
  displayUrl: string;
  /** Reclaims the host seat from another device. */
  hostResumeToken: string;
}

/** A category tag in the public bank, with how many questions carry it. */
export interface PlayNowCategory {
  tag: string;
  count: number;
}

/** What Play Now hands the creator: a join code/link and the control token to start it. */
export interface CreatedPlayNow {
  code: string;
  title: string;
  joinUrl: string;
  /** The control credential; keep it in the creator's tab only. */
  creatorToken: string;
  /** Cells the bank filled, and cells it could not. */
  clues: number;
  gaps: number;
}

/** The setup choices for a Play Now game. */
/** A finished Play Now game, question by question (`session/playNow.ts` `playNowReview`). */
export interface PlayNowReview {
  players: {
    participantId: string;
    name: string;
    correct: number;
    wrong: number;
    unanswered: number;
    /** Speed-bonus points across the game, already inside the score. */
    speedBonus: number;
  }[];
  questions: {
    clueId: string;
    category: string;
    prompt: string;
    answer: string;
    answers: {
      participantId: string;
      text: string | null;
      verdict: 'correct' | 'incorrect' | null;
      points: number;
      bonus: number;
    }[];
  }[];
}

export interface Friend {
  id: string;
  displayName: string;
  since?: number;
}

/** Someone who added you; `added` is whether you added them back. */
export interface AddedYou extends Friend {
  added: boolean;
}

export type LeaderboardPeriod = 'day' | 'week' | 'lifetime';

export interface Leaderboard {
  period: LeaderboardPeriod;
  scope: 'everyone' | 'friends';
  /** `friend`: the viewer added this account. */
  rows: { accountId: string; displayName: string; points: number; games: number; rank: number; friend: boolean }[];
  /** The viewer's account id, to mark their row. */
  you: string;
}

export interface PlayNowRequest {
  template: 'quick' | 'board' | 'pub';
  answerMode: 'highScore' | 'buzzer';
  losePoints?: boolean;
  speedBonus?: boolean;
  categories?: string[];
  /** Play Again: the finished game this one replaces; its players follow here. */
  rematchOf?: { code: string; creatorToken: string };
}

export const api = {
  // --- Play Now (public; no account) ---------------------------------------
  getPlayNowCategories: () =>
    request<{ categories: PlayNowCategory[] }>('/play-now/categories'),
  createPlayNow: (body: PlayNowRequest) =>
    request<CreatedPlayNow>('/play-now', { method: 'POST', body: JSON.stringify(body) }),
  getFriends: () => request<{ code: string; friends: Friend[]; addedYou: AddedYou[] }>('/friends'),
  /** A new friend link; the old one stops working. */
  newFriendCode: () => request<{ code: string }>('/friends/code', { method: 'POST' }),
  addFriend: (code: string) =>
    request<{ friend: Friend }>('/friends', { method: 'POST', body: JSON.stringify({ code }) }),
  /** One way: they are not asked, and see you on their "added you" list. */
  addFriendById: (accountId: string) =>
    request<{ friend: Friend }>('/friends', { method: 'POST', body: JSON.stringify({ accountId }) }),
  removeFriend: (id: string) => request<void>(`/friends/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  siteStats: () => request<SiteReport>('/admin/stats'),

  getLeaderboard: (period: LeaderboardPeriod, friends: boolean) =>
    request<Leaderboard>(`/leaderboard?period=${period}${friends ? '&scope=friends' : ''}`),
  /** A finished game's answers. The seat token rides in the body, never the URL. */
  getPlayNowReview: (code: string, token: string | null) =>
    request<PlayNowReview>(`/play-now/${encodeURIComponent(code)}/review`, {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  /** Start the game on the creator's token. Idempotent; 204 on success. */
  startPlayNow: (code: string, creatorToken: string) =>
    request<void>(`/play-now/${encodeURIComponent(code)}/start`, {
      method: 'POST',
      headers: { 'x-creator-token': creatorToken },
      body: JSON.stringify({ creatorToken }),
    }),

  // --- the registry --------------------------------------------------------
  listGames: () => request<GameManifestSummary[]>('/games'),

  /** Lets the create screen hide a `needsContent` game that has no pack; `mine` is the account screen's list. */
  listPacks: (opts?: { gameId?: string; mine?: boolean }) => {
    const params = new URLSearchParams();
    if (opts?.gameId !== undefined) params.set('gameId', opts.gameId);
    if (opts?.mine) params.set('mine', '1');
    const query = params.toString();
    return request<PackSummary[]>(query ? `/packs?${query}` : '/packs');
  },

  // --- show formats; presets are rows on this list ----------------------------

  /** `mine` is the account screen's "templates I created"; without it, presets plus everyone's. */
  listFormats: (mine?: boolean) => request<FormatSummary[]>(mine ? '/formats?mine=1' : '/formats'),
  /** An unhosted show can only start from a format, so the builder saves one. */
  saveFormat: (draft: { title: string; description?: string; steps: StepDraft[] }) =>
    request<FormatSummary>('/formats', { method: 'POST', body: JSON.stringify(draft) }),
  deleteFormat: (id: string) =>
    request<void>(`/formats/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // --- sessions ------------------------------------------------------------
  listSessions: () => request<SessionSummary[]>('/sessions'),
  /**
   * Start a show. The create screen always passes `formatId`; `gameId` alone is kept for
   * e2e specs that drive a single game.
   */
  createSession: (title: string, gameId?: string, formatId?: string) =>
    request<CreatedSession>('/sessions', {
      method: 'POST',
      body: JSON.stringify({ title, gameId, formatId }),
    }),
  /** Separate from `getSession` because that route is public and this is a credential. */
  getDisplayLink: (code: string) =>
    request<{ code: string; displayToken: string; displayUrl: string }>(
      `/sessions/${encodeURIComponent(code)}/display`,
    ),
  getSession: (code: string) =>
    request<{
      code: string;
      title: string;
      status: string;
      gameId: string;
      playerCount: number;
      joinUrl: string;
    }>(`/sessions/${encodeURIComponent(code)}`),
  deleteSession: (code: string) =>
    request<void>(`/sessions/${encodeURIComponent(code)}`, { method: 'DELETE' }),
  qrDataUrl: (code: string) =>
    request<{ url: string; dataUrl: string }>(
      `/sessions/${encodeURIComponent(code)}/qr?format=dataurl`,
    ),

  /** Hand clue control to the TV, or take it back. Actor authority is core's, so HTTP. */
  setDisplayCanDrive: (code: string, on: boolean) =>
    request<{ code: string; displayCanDrive: boolean }>(
      `/sessions/${encodeURIComponent(code)}`,
      { method: 'PATCH', body: JSON.stringify({ displayCanDrive: on }) },
    ),

  /** Rename the game. Broadcasts, so the TV and any second host tab follow. */
  renameSession: (code: string, title: string) =>
    request<{ code: string; title: string }>(
      `/sessions/${encodeURIComponent(code)}`,
      { method: 'PATCH', body: JSON.stringify({ title }) },
    ),

  /** Hand the party-leader badge to a phone. Refused on a hosted show. */
  setPartyLeader: (code: string, participantId: string | null) =>
    request<{ code: string; partyLeaderId: string | null }>(
      `/sessions/${encodeURIComponent(code)}`,
      { method: 'PATCH', body: JSON.stringify({ partyLeaderId: participantId }) },
    ),

  // --- the ledger and participant admin ------------------------------------
  adjustScore: (code: string, participantId: string, delta: number) =>
    request<void>(`/sessions/${encodeURIComponent(code)}/scores`, {
      method: 'POST',
      body: JSON.stringify({ participantId, delta }),
    }),
  renamePlayer: (code: string, participantId: string, name: string) =>
    request<void>(
      `/sessions/${encodeURIComponent(code)}/participants/${encodeURIComponent(participantId)}`,
      { method: 'PATCH', body: JSON.stringify({ name }) },
    ),
  removePlayer: (code: string, participantId: string) =>
    request<void>(
      `/sessions/${encodeURIComponent(code)}/participants/${encodeURIComponent(participantId)}`,
      { method: 'DELETE' },
    ),
  /** The room toggles; either or both. */
  setRoom: (code: string, room: { locked?: boolean; profanityFilter?: boolean }) =>
    request<{ code: string }>(`/sessions/${encodeURIComponent(code)}`, {
      method: 'PATCH',
      body: JSON.stringify(room),
    }),
  /** A kicked phone asks back in; the same call reports the answer. Public. */
  requestRejoin: (code: string, token: string) =>
    request<{ status: 'kicked' | 'pending' | 'denied' | 'admitted' }>(
      `/sessions/${encodeURIComponent(code)}/rejoin`,
      { method: 'POST', body: JSON.stringify({ token }) },
    ),
  decideRejoin: (code: string, requestId: string, admit: boolean) =>
    request<void>(
      `/sessions/${encodeURIComponent(code)}/rejoin-requests/${encodeURIComponent(requestId)}`,
      { method: 'POST', body: JSON.stringify({ admit }) },
    ),
  mergePlayers: (code: string, fromId: string, intoId: string) =>
    request<void>(
      `/sessions/${encodeURIComponent(code)}/participants/${encodeURIComponent(fromId)}/merge`,
      { method: 'POST', body: JSON.stringify({ intoId }) },
    ),
};
